import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/utils/api";

interface CloseoutRow {
  id: string;
  request_number: string;
  requestor: string;
  status: string;
  needed_by_date: string;
  updated_at: string;
}

export function CloseoutPage() {
  const [requests, setRequests] = useState<CloseoutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    const data = await apiFetch<{ requests: CloseoutRow[] }>("/closeout");
    setRequests(data.requests);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const closeRequest = async (id: string) => {
    try {
      await apiFetch(`/equipment-requests/${id}/close`, { method: "POST" });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Close failed");
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Closeout"
        subtitle="Shipping records ready to be closed after fulfillment or delivery."
      />
      <DataTable
        columns={[
          {
            key: "num",
            header: "Shipping #",
            render: (r) => (
              <Link to={`/equipment-request/${r.id}`} className="font-medium text-[#1e3a4f] hover:underline">
                {r.request_number}
              </Link>
            ),
          },
          { key: "requestor", header: "Requestor", render: (r) => r.requestor },
          { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { key: "needed", header: "Needed By", render: (r) => r.needed_by_date || "—" },
          {
            key: "action",
            header: "Action",
            render: (r) => (
              <Button size="sm" onClick={() => closeRequest(r.id)} className="bg-[#1e3a4f] hover:bg-[#163041]">
                Close
              </Button>
            ),
          },
        ]}
        rows={requests}
        rowKey={(r) => r.id}
        emptyMessage="No shipping records pending closeout."
      />
    </div>
  );
}
